/**
 * useAiFeaturesEnabled — is the AI-tutor feature switched on in THIS environment?
 *
 * Single source of truth is the backend's YUDU_AI_FEATURES_ENABLED kill switch
 * (the same flag that mounts / 404s the entire /api/ai/* tree). We read it at
 * runtime from the public, unauthenticated endpoint:
 *
 *   GET /api/public/features  ->  { ai_tutor: boolean, content_fanout: boolean }
 *
 * so the tutor nav tabs appear exactly where the tutor backend actually works
 * and stay hidden in production while the feature is pre-release — with no
 * per-environment frontend env var to keep in sync.
 *
 * Defaults to FALSE while loading and on any fetch error: an unreleased feature
 * must never flash into view, and a soft failure should hide, not reveal. The
 * last answer is cached in localStorage (10 min TTL) and used as the initial
 * state, so repeat navigations don't re-flicker.
 *
 * The cache + fetch/dedup logic lives in services/shared/featureFlags.ts,
 * shared with the plain `isContentFanoutEnabled()` getter that gates the
 * content-fanout create paths (courseJourney.ts) — both flags come back on
 * the SAME request and are cached together under `features:v2`. This hook's
 * public API (a plain `boolean`) is unchanged for its existing consumers.
 */
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '../services/queryClient';
import { fetchFlags, readCachedFlags } from '../services/shared/featureFlags';

export function useAiFeaturesEnabled(): boolean {
  const query = useQuery(
    {
      queryKey: ["features", "flags"],
      queryFn: fetchFlags,
      staleTime: 0,
      retry: false,
    },
    queryClient,
  );

  return query.data?.aiTutor ?? readCachedFlags()?.aiTutor ?? false;
}
